<template>
	<div>
		<div class="mb-4">
			<h6 class="text-lg uppercase tracking-wider text-gray-500 mb-1">Notificari update HUB</h6>
		</div>

		<!-- ghost points the local app at baza de date de productie, so it says so before anything is written -->
		<div class="rounded-lg p-3 mb-4 text-sm" :class="onProductionDb
			? 'bg-red-50 border border-red-300 text-red-700'
			: 'bg-gray-50 border border-gray-200 text-gray-600'">
			<v-icon small :color="onProductionDb ? 'red' : 'grey'" class="mr-1">
				{{ onProductionDb ? "mdi-alert" : "mdi-database" }}
			</v-icon>
			<span v-if="onProductionDb" class="font-bold">
				Baza de date de PRODUCTIE ({{ apiHost }}) &mdash; update-urile salvate aici ajung la
				utilizatorii reali.
			</span>
			<span v-else>Baza de date locala ({{ apiHost }}).</span>
		</div>

		<div v-if="loading" class="progress">
			<div class="indeterminate"></div>
		</div>

		<div v-else-if="error">
			<p class="text-red-500 mb-4">{{ error }}</p>
			<v-btn color="teal" class="uppercase white--text" @click="load">Reincarca</v-btn>
		</div>

		<div v-else>
			<!-- Compose -->
			<v-form ref="formUpdate" v-model="validForm" class="bg-white border rounded-lg p-4 mb-6"
				@submit.prevent="submit">
				<div class="flex justify-between items-center mb-4">
					<h6 class="text-xl font-bold mb-0">
						{{ editingId ? "Modifica update #" + editingId : "Update nou" }}
					</h6>
					<v-btn v-if="editingId" small text class="uppercase" @click="resetForm">
						<v-icon left small>mdi-close</v-icon>Renunta la modificare
					</v-btn>
				</div>

				<!-- both start filled with the defaults; the placeholders are what a cleared field shows -->
				<v-text-field v-model="form.title" label="Titlu" dense outlined hide-details="auto" class="mb-3"
					:placeholder="defaultTitle" :rules="[rules.required, rules.short]"></v-text-field>

				<v-text-field v-model="form.subtitle" label="Subtitlu" dense outlined hide-details="auto" class="mb-3"
					:placeholder="defaultSubtitle" :rules="[rules.short]"></v-text-field>

				<v-textarea v-model="form.description" label="Descriere" dense outlined rows="3" hide-details="auto"
					class="mb-3" placeholder="ce s-a schimbat"></v-textarea>

				<v-switch v-model="form.is_global" :true-value="1" :false-value="0" color="teal" class="mt-0 mb-2"
					hide-details label="Global - toti utilizatorii HUB"></v-switch>

				<!-- a global update is handed to every user by the API, so there is nothing to pick -->
				<div v-if="!form.is_global">
					<v-select v-model="form.users" :items="userItems" :loading="loadingUsers" label="Destinatari"
						multiple chips deletable-chips dense outlined hide-details="auto" class="mb-2"
						:rules="[rules.recipients]"></v-select>

					<div class="flex gap-2 mb-2">
						<v-btn x-small text class="uppercase" @click="selectAllUsers">Toti</v-btn>
						<v-btn x-small text class="uppercase" @click="form.users = []">Niciunul</v-btn>
					</div>
				</div>

				<!-- what the bell will show, laid out the way the navbar lays it out -->
				<div class="bg-gray-50 border rounded p-3 mb-4">
					<p class="text-xs uppercase tracking-wider text-gray-500 mb-2">Previzualizare</p>
					<p class="font-medium mb-0">{{ form.title || "Titlu" }}</p>
					<p class="text-sm text-gray-600 mb-0">{{ form.subtitle }}</p>
					<p class="text-sm text-primary mb-0">{{ form.description }}</p>
					<p class="text-xs text-gray-500 mt-2 mb-0">
						Ajunge la {{ form.is_global ? "toti utilizatorii" : form.users.length + " utilizatori" }}.
					</p>
				</div>

				<div class="flex justify-end">
					<v-btn type="submit" color="teal" class="uppercase white--text" :loading="saving"
						:disabled="saving">
						{{ editingId ? "Salveaza modificarile" : "Trimite update" }}
					</v-btn>
				</div>
			</v-form>

			<!-- Existing updates: the most recent ones, scrolled sideways -->
			<div class="flex items-baseline justify-between mb-3">
				<h6 class="text-lg font-bold mb-0">Ultimele {{ updatesLimit }} update-uri</h6>
				<span v-if="updates.length > updatesVisible" class="text-xs text-gray-400">
					{{ updatesVisible }} din {{ updates.length }} &mdash; deruleaza orizontal &rarr;
				</span>
			</div>

			<p v-if="updates.length < 1" class="text-gray-500">Nu exista update-uri.</p>

			<!-- the side padding keeps a card's border off the scroll edge, the negative margin
			     puts the row back in line with the rest of the page -->
			<div v-else class="updates-row flex items-stretch gap-4 overflow-x-auto pb-3 -mx-1 px-1">
				<div v-for="update in updates" :key="update.id_update"
					class="update-card bg-white border rounded-lg p-4 flex flex-col"
					:class="{ 'border-primary': editingId == update.id_update }">
					<div class="flex items-center justify-between gap-1 mb-2">
						<div class="flex items-center gap-2 min-w-0">
							<v-chip x-small :color="update.is_global ? 'teal' : 'blue-grey'" text-color="white">
								{{ update.is_global ? "global" : "tintit" }}
							</v-chip>
							<span class="text-xs text-gray-400">#{{ update.id_update }}</span>
						</div>

						<div class="flex flex-shrink-0">
							<v-btn icon x-small title="Modifica" @click="edit(update)">
								<v-icon small>mdi-pencil</v-icon>
							</v-btn>
							<v-btn icon x-small title="Sterge update-ul" @click="confirmDelete(update)">
								<v-icon small>mdi-delete</v-icon>
							</v-btn>
						</div>
					</div>

					<!-- the full text is on the title attribute, since a card truncates it -->
					<p class="font-bold mb-1 truncate" :title="update.title">{{ update.title }}</p>
					<p class="text-sm text-gray-600 mb-1 truncate" :title="update.subtitle">{{ update.subtitle }}</p>
					<p class="text-sm text-gray-600 mb-2 clamp-3" :title="update.description">
						{{ update.description }}
					</p>

					<!-- mt-auto pins the footer down, so cards of different text length line up -->
					<div class="mt-auto pt-2 border-t">
						<p class="text-xs text-gray-400 mb-1">{{ update.created_at | formatDateTime }}</p>

						<!-- a menu rather than an expander: a card that grows would stretch the whole row -->
						<v-menu offset-y max-height="320" :close-on-content-click="false">
							<template v-slot:activator="{ on, attrs }">
								<v-btn x-small text class="px-1" v-bind="attrs" v-on="on">
									<v-icon x-small left>mdi-account-multiple</v-icon>
									citit de {{ update.read_count }} din {{ update.recipients.length }}
								</v-btn>
							</template>
							<v-list dense class="py-1">
								<v-list-item v-for="recipient in update.recipients" :key="recipient.user_id">
									<div class="flex items-center gap-2 text-sm">
										<v-icon x-small :color="recipient.is_read ? 'green' : 'grey'">
											{{ recipient.is_read ? "mdi-check" : "mdi-circle-outline" }}
										</v-icon>
										<span>{{ recipient.username }}</span>
										<span v-if="recipient.read_at" class="text-xs text-gray-400">
											{{ recipient.read_at | formatDateTime }}
										</span>
									</div>
								</v-list-item>
							</v-list>
						</v-menu>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
/**
 * Authoring the in-app updates - what the navbar bell shows.
 *
 * An update reaches a user through a UserUpdates row, so the recipients are chosen here and written
 * with the update. Editing one leaves the recipients that stay untouched, read state included: a
 * corrected typo should not put the notification back in front of somebody who already dismissed it.
 *
 * Dev only, wired up in Setari.vue - `npm run ghost` points the local app at the production
 * database, which is how a real update gets written.
 */

/**
 * How many of the most recent updates to load. The API returns each one with its recipients, and a
 * global update carries a row per user, so this is what is asked for rather than what is shown.
 */
const UPDATES_LIMIT = 5;

/** How many cards the row fits - kept with `.update-card`'s width, which is what decides it. */
const UPDATES_VISIBLE = 3;

/**
 * What a routine update announcement says, prefilled - most of them are a version bump and differ
 * only in the description.
 *
 * The version is the one the app is running, straight from package.json: the version is bumped
 * first and the update announcing it is written afterwards, so by the time this screen is open the
 * running version is already the one being announced.
 */
const DEFAULT_TITLE = "update HUB cuprolli";
const defaultSubtitle = version => "v." + version + " - refresh la pagina";

export default {
	name: "SettingsUpdates",
	data() {
		return {
			updatesLimit: UPDATES_LIMIT,
			updatesVisible: UPDATES_VISIBLE,
			validForm: false,
			// null while composing a new update, the id_update being edited otherwise
			editingId: null,
			form: this.blankForm(),
			rules: {
				required: value => (value !== "" && value !== null && value !== undefined)
					|| "Câmp obligatoriu",
				short: value => String(value || "").length <= 255 || "Maxim 255 de caractere",
				recipients: value => (Array.isArray(value) && value.length > 0)
					|| "Alege cel putin un destinatar",
			},
		};
	},
	computed: {
		section() {
			return this.$store.state.updates;
		},
		loading() {
			return this.section.loadingUpdates;
		},
		saving() {
			return this.section.savingUpdate;
		},
		error() {
			return this.section.updatesError;
		},
		updates() {
			return this.section.updates;
		},
		users() {
			return this.$store.state.userss.users;
		},
		loadingUsers() {
			return this.$store.state.userss.loadingUsers;
		},
		userItems() {
			return this.users.map(user => ({
				value: user.id_user,
				text: user.username + (user.rol ? " (" + user.rol + ")" : "")
			}));
		},
		defaultTitle() {
			return DEFAULT_TITLE;
		},
		/** The same text the form starts with, so a cleared field still shows what is expected. */
		defaultSubtitle() {
			return defaultSubtitle(this.$store.getters.version);
		},
		apiHost() {
			return process.env.VUE_APP_HUB_API_HOST;
		},
		/** ghost mode runs locally against the live API, so the warning follows the host, not the build. */
		onProductionDb() {
			return !/localhost|127\.0\.0\.1/.test(this.apiHost || "");
		},
	},
	methods: {
		/**
		 * Called from data(), where the computed properties do not exist yet - hence the constants
		 * rather than `this.defaultTitle`. The store is already on the instance by then.
		 */
		blankForm() {
			return {
				title: DEFAULT_TITLE,
				subtitle: defaultSubtitle(this.$store.getters.version),
				description: "",
				is_global: 1,
				users: []
			};
		},
		load() {
			this.$store.dispatch("updates/fetchUpdates", UPDATES_LIMIT);
			this.$store.dispatch("userss/fetchUsers", {});
		},
		resetForm() {
			this.editingId = null;
			this.form = this.blankForm();

			if (this.$refs.formUpdate) this.$refs.formUpdate.resetValidation();
		},
		edit(update) {
			this.editingId = update.id_update;
			this.form = {
				title: update.title,
				subtitle: update.subtitle || "",
				description: update.description || "",
				is_global: update.is_global,
				users: update.recipients.map(recipient => recipient.user_id)
			};

			this.$vuetify.goTo(0);
		},
		selectAllUsers() {
			this.form.users = this.users.map(user => user.id_user);
		},
		async submit() {
			const valid = await this.$refs.formUpdate.validate();

			if (!valid) return;

			const payload = {
				title: this.form.title.trim(),
				subtitle: this.form.subtitle.trim(),
				description: this.form.description.trim(),
				is_global: this.form.is_global,
				// a global update is handed to everybody by the API; sending a list would only
				// describe who happened to be selected before the switch was turned on
				users: this.form.is_global ? [] : this.form.users
			};

			const saved = this.editingId
				? await this.$store.dispatch("updates/saveUpdate",
					Object.assign({ id: this.editingId }, payload))
				: await this.$store.dispatch("updates/createUpdate", payload);

			if (saved) this.resetForm();
		},
		confirmDelete(update) {
			const question = `Stergi update-ul "${update.title}" si cele `
				+ `${update.recipients.length} notificari ale lui?`;

			if (window.confirm(question)) {
				this.$store.dispatch("updates/deleteUpdate", update.id_update);

				if (this.editingId === update.id_update) this.resetForm();
			}
		},
	},
	created() {
		this.load();
	},
};
</script>

<style scoped>
/*
 * Three cards fill the row, the rest are scrolled to - the point of the row being scrollable is
 * that you can see there is more than what fits. The 2rem is the two gap-4 gaps between the three,
 * and the min-width lets a narrow settings pane drop to fewer rather than squash them.
 */
.update-card {
	flex: 0 0 calc((100% - 2rem) / 3);
	min-width: 15rem;
	scroll-snap-align: start;
}

.updates-row {
	scroll-snap-type: x proximity;
}

/* Tailwind 2 ships no line-clamp, and a card in the row needs the description to stop somewhere */
.clamp-3 {
	display: -webkit-box;
	-webkit-line-clamp: 3;
	line-clamp: 3;
	-webkit-box-orient: vertical;
	overflow: hidden;
}
</style>
